import { ContentState, EditorState, Modifier, SelectionState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';

import { normalizeRichTextEditorValue } from './richTextEditorValue';

const getSavedHtml = (contentState: ContentState): string =>
  normalizeRichTextEditorValue(contentState.getPlainText(), draftToHtml(convertToRaw(contentState)));

/**
 * Length of the string the editor saves on blur: the draftToHtml output as normalised by
 * normalizeRichTextEditorValue. Tags and escaped entities count, not only the visible text.
 */
export function getHtmlLength(editorState: EditorState): number {
  return getSavedHtml(editorState.getCurrentContent()).length;
}

const isHighSurrogate = (code: number) => code >= 0xd800 && code <= 0xdbff;
const isLowSurrogate = (code: number) => code >= 0xdc00 && code <= 0xdfff;

/**
 * Keeps the first `charCount` characters of the document text (blocks are separated by one character,
 * as in ContentState.getPlainText) and removes the rest, so tags are re-rendered rather than cut.
 */
function keepTextPrefix(contentState: ContentState, charCount: number): ContentState {
  const blocks = contentState.getBlockMap().toArray();
  let remaining = charCount;

  for (let index = 0; index < blocks.length; index++) {
    const block = blocks[index];
    const length = block.getLength();
    if (remaining <= length) {
      let offset = remaining;
      const text = block.getText();
      // Never split a surrogate pair: a lone surrogate is not valid text.
      if (
        offset > 0 &&
        offset < length &&
        isHighSurrogate(text.charCodeAt(offset - 1)) &&
        isLowSurrogate(text.charCodeAt(offset))
      ) {
        offset -= 1;
      }

      const lastBlock = blocks[blocks.length - 1];
      const removal = SelectionState.createEmpty(block.getKey()).merge({
        anchorOffset: offset,
        focusKey: lastBlock.getKey(),
        focusOffset: lastBlock.getLength(),
      }) as SelectionState;

      return Modifier.removeRange(contentState, removal, 'forward');
    }
    remaining -= length + 1;
  }

  return contentState;
}

/**
 * Returns the editor state whose saved HTML is at most `maxLength`. When the content is too long, the
 * text is shortened to the largest prefix that fits and the HTML is rendered again from that text.
 */
export function truncateToHtmlLimit(
  editorState: EditorState,
  maxLength: number
): { editorState: EditorState; truncated: boolean } {
  const contentState = editorState.getCurrentContent();
  if (getSavedHtml(contentState).length <= maxLength) {
    return { editorState, truncated: false };
  }

  // Longer text never serialises to shorter HTML, so the largest fitting prefix can be found by bisection.
  let low = 0;
  let high = contentState.getPlainText().length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (getSavedHtml(keepTextPrefix(contentState, middle)).length <= maxLength) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }

  const truncatedContent = keepTextPrefix(contentState, low);
  const pushed = EditorState.push(editorState, truncatedContent, 'remove-range');
  return { editorState: EditorState.moveFocusToEnd(pushed), truncated: true };
}
